import { supabase } from '@/services/supabaseClient';
import type { User } from '@/db';
import { canView, statuses, type RequestRecord } from './model';

interface RequestRow {
  id: string; protocol: string; kind: string; owner: string; division: string;
  status: string; data: Record<string, string>; project_id: string | null;
  revision: number; history: RequestRecord['history'];
  created_at: string; updated_at: string;
}

function mapRow(row: RequestRow): RequestRecord {
  return {
    id: row.id, protocol: row.protocol, kind: row.kind as RequestRecord['kind'], owner: row.owner,
    division: row.division, status: row.status, data: row.data, projectId: row.project_id ?? undefined,
    revision: row.revision, history: row.history, createdAt: row.created_at, updatedAt: row.updated_at,
  };
}

export async function listRequests(): Promise<RequestRecord[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from('zsb_requests').select('*').order('created_at', { ascending: false });
  if (error) throw new Error('Não foi possível carregar as solicitações.');
  return (data as RequestRow[]).map(mapRow);
}

export async function saveRequest(record: RequestRecord, user: User): Promise<RequestRecord> {
  if (!supabase) throw new Error('Supabase não configurado.');
  if (!statuses[record.kind].includes(record.status)) throw new Error('Status inválido.');

  const { data: existingRows } = await supabase.from('zsb_requests').select('*').eq('id', record.id).limit(1);
  const previous = existingRows && existingRows.length ? mapRow(existingRows[0] as RequestRow) : null;

  if (previous && !canView(previous, user)) throw new Error('Registro alterado em outra janela ou sem permissão. Feche e abra novamente.');
  if (!previous && record.owner !== user.email.toLowerCase()) throw new Error('Responsável inválido.');

  if (record.projectId) {
    const { data: projectRows } = await supabase.from('zsb_requests').select('*').eq('id', record.projectId).limit(1);
    const project = projectRows && projectRows.length ? mapRow(projectRows[0] as RequestRow) : null;
    if (!project || project.kind !== 'projeto' || !canView(project, user)) throw new Error('Projeto vinculado indisponível.');
  }

  const payload = {
    protocol: record.protocol, kind: record.kind, division: record.division, status: record.status,
    data: record.data, project_id: record.projectId ?? null, history: record.history,
  };

  if (!previous) {
    const { data: inserted, error } = await supabase.from('zsb_requests')
      .insert({ id: record.id, owner: record.owner, revision: 1, ...payload })
      .select().single();
    if (error) throw new Error('Não foi possível salvar. Seus dados permanecem no formulário.');
    return mapRow(inserted as RequestRow);
  }

  const { data: updated, error } = await supabase.from('zsb_requests')
    .update({ ...payload, revision: previous.revision + 1, updated_at: new Date().toISOString() })
    .eq('id', record.id).eq('revision', previous.revision)
    .select();
  if (error) throw new Error('Não foi possível salvar. Seus dados permanecem no formulário.');
  if (!updated || updated.length === 0) throw new Error('Registro alterado em outra janela ou sem permissão. Feche e abra novamente.');
  return mapRow(updated[0] as RequestRow);
}
